import React from 'react';
import { Box, HStack, Input, Button, Text, Avatar, Icon, Spinner } from '@chakra-ui/react';
import { FiUserPlus } from 'react-icons/fi';

/**
 * Pick a person, without downloading the workspace to do it.
 *
 * ★ WHY THIS EXISTS RATHER THAN ANOTHER COPY OF `SearchUsers`.
 * The picker most of this product uses takes the ENTIRE user array as a prop and
 * filters it in the browser — it is imported by fifteen files across eight
 * feature areas, and every one of them downloads every user in the tenant to let
 * someone choose one name. That is survivable at twenty users and not at two
 * hundred. This one asks the server, one debounced page at a time.
 *
 * ★ IT DOES NOT FETCH. The caller supplies `useSearch` and gets to choose
 * which endpoint answers, which permission guards it, and what a row means. A
 * picker that hard-wired its own endpoint would be a fourth copy the moment a
 * second screen needed a different one.
 *
 * ★ `useSearch` IS A HOOK, AND THE NAME IS THE CONTRACT.
 * It is called here once, unconditionally, on every render — which is what lets
 * a caller put `useQuery` inside it. That only stays true because the call is
 * not guarded by anything; wrapping it in a condition, a loop or a callback
 * would break every caller silently. Hence the name: the linter enforces on
 * `use*` what this comment could otherwise only ask for.
 *
 * ★ ROWS CARRY THEIR OWN WARNINGS. `renderMeta` is how a caller says something
 * about a candidate BEFORE the choice is made — "this person's role grants them
 * no inbox" — rather than letting an administrator pick someone and discover it
 * afterwards. Such people are still offered: hiding them would hide the problem.
 */

/** A tiny debounce, so the search does not fire on every keystroke. */
const useDebounced = (value, ms) => {
	const [v, setV] = React.useState(value);
	React.useEffect(() => {
		const t = setTimeout(() => setV(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return v;
};

export const userLabel = (u) =>
	`${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || u?.fullName || u?.username || u?.email || 'Unknown user';

/**
 * @param {object} props
 * @param {(query: string, page: number, isOpen: boolean) => {users, pagination, isFetching}} props.useSearch
 *   A HOOK returning the current page. Given the debounced query, the page, and
 *   whether the list is open — so a caller can skip the request while it is not.
 * @param {(user: object) => void} props.onSelect
 * @param {(user: object) => React.ReactNode} [props.renderMeta]  a badge per row
 * @param {(user: object) => string} [props.renderHint]  the second line
 * @param {string} [props.placeholder]
 * @param {string} [props.emptyText]     nothing matched the query
 * @param {string} [props.exhaustedText] nothing to offer at all
 * @param {boolean} [props.isBusy]       the caller's own mutation is in flight
 */
export default function UserPicker({
	useSearch,
	onSelect,
	renderMeta,
	renderHint,
	placeholder = 'Search by name or email…',
	emptyText = 'No one matches that.',
	exhaustedText = 'Nobody left to choose.',
	isBusy = false,
	inputProps = {},
}) {
	const [query, setQuery] = React.useState('');
	const [open, setOpen] = React.useState(false);
	const [page, setPage] = React.useState(1);
	const debounced = useDebounced(query, 250);

	// A new search starts at the beginning; staying on page 4 of the previous
	// query would show an empty list and look like "no matches".
	React.useEffect(() => setPage(1), [debounced]);

	// Unconditional, exactly once — see the contract above.
	const { users = [], pagination = null, isFetching = false } =
		useSearch(debounced.trim(), page, open) ?? {};

	const total = pagination?.total ?? users.length;
	const hasMore = Boolean(pagination?.hasNextPage);
	// Derived from the page the server actually served, not from local state, so
	// a clamped or defaulted limit is reflected rather than assumed.
	const perPage = pagination?.limit ?? users.length;
	const firstShown = users.length ? (pagination?.page ?? 1) * perPage - perPage + 1 : 0;
	const lastShown = firstShown + users.length - 1;

	const choose = (user) => {
		onSelect(user);
		setQuery('');
		setPage(1);
		setOpen(false);
	};

	return (
		<Box position="relative">
			<HStack spacing="2">
				<Input
					size="sm"
					bg="bg.input"
					placeholder={placeholder}
					value={query}
					onFocus={() => setOpen(true)}
					onChange={(e) => {
						setQuery(e.target.value);
						setOpen(true);
					}}
					aria-label={placeholder}
					{...inputProps}
				/>
				{open && (
					<Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
						Done
					</Button>
				)}
			</HStack>

			{open && (
				<Box
					position="absolute"
					zIndex="dropdown"
					mt="1"
					w="100%"
					maxH="260px"
					overflowY="auto"
					bg="bg.surface"
					borderWidth="1px"
					borderColor="border.default"
					borderRadius="md"
					boxShadow="hover"
				>
					{isFetching && users.length === 0 && (
						<Box p="3">
							<Spinner size="xs" />
						</Box>
					)}
					{!isFetching && users.length === 0 && (
						<Text p="3" fontSize="xs" color="text.muted">
							{debounced ? emptyText : exhaustedText}
						</Text>
					)}

					{users.map((user) => (
						<HStack
							key={user._id}
							px="3"
							py="2"
							spacing="2"
							cursor="pointer"
							_hover={{ bg: 'bg.elevated' }}
							onClick={() => choose(user)}
							role="button"
							aria-label={`Select ${userLabel(user)}`}
						>
							<Avatar size="xs" name={userLabel(user)} src={user.profileImage} />
							<Box minW="0" flex="1">
								<HStack spacing="1.5">
									<Text fontSize="sm" color="text.primary" noOfLines={1}>
										{userLabel(user)}
									</Text>
									{renderMeta?.(user)}
								</HStack>
								<Text fontSize="0.65rem" color="text.muted" noOfLines={1}>
									{renderHint ? renderHint(user) : user.email}
								</Text>
							</Box>
							<Icon as={FiUserPlus} color="text.muted" />
						</HStack>
					))}

					{/* ★ THE COUNT IS WHAT MAKES A TRUNCATION VISIBLE.
					    A capped list with no total reads as "these are all of them", so
					    an administrator searching for someone further down the alphabet
					    concludes they are absent rather than unshown. The RANGE matters
					    as much as the total: on page two, "showing 10 of 25" would be
					    true and still leave you unable to tell which ten. */}
					{users.length > 0 && total > users.length && (
						<HStack
							px="3"
							py="2"
							spacing="2"
							justify="space-between"
							borderTopWidth="1px"
							borderColor="border.subtle"
							position="sticky"
							bottom="0"
							bg="bg.surface"
						>
							<Text fontSize="0.65rem" color="text.muted" noOfLines={1}>
								{firstShown}–{lastShown} of {total}
								{!debounced && ' · type to narrow'}
							</Text>
							<HStack spacing="1">
								<Button
									size="xs"
									variant="ghost"
									isDisabled={page <= 1 || isFetching}
									onClick={() => setPage((n) => Math.max(n - 1, 1))}
								>
									Prev
								</Button>
								<Button
									size="xs"
									variant="ghost"
									isDisabled={!hasMore}
									isLoading={isFetching}
									onClick={() => setPage((n) => n + 1)}
								>
									Next
								</Button>
							</HStack>
						</HStack>
					)}

					{isBusy && (
						<Box p="2">
							<Spinner size="xs" />
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
